// @ts-nocheck
// Inbox selectors — the single source of truth for "what's in the inbox".
//
// The inbox is ticket-centric: every ticket is a customer conversation, so
// each one is one inbox entry. When a ticket's comment thread has been loaded
// (the detail screen does this on open) we surface the latest customer message
// as the snippet; otherwise we fall back to the ticket subject. This keeps the
// inbox — and the sidebar/bell badges derived from it — honest without forcing
// an N+1 fetch of every ticket's comments up front.
//
// Both the Inbox screen and the sidebar import from here so their counts can
// never drift apart.

import type { Ticket, Comment } from '../schema/types';

export interface InboxItem {
  id: string;            // ticket-centric read key (the ticket id)
  kind: 'message';
  ticketId: string;
  ticketSubject: string;
  channel: string;
  sender: { name: string; email: string; initials: string; color: string };
  snippet: string;
  time: string;            // relative last-activity label ("8 days ago") — from API
  timeISO: string | null;  // raw API timestamp behind `time` (for exact tooltip)
  viaEmail: boolean;
  unread: boolean;
  priority: string;
  watcherBounce: boolean;
  commentCount: number;
  assigneeName: string | null;   // who the ticket is assigned to (display name)
  assigneeInitials: string | null;
  assigneeColor: string | null;
  assignedToMe: boolean;         // true when assigned to the viewing user
  // High-level ticket facts surfaced on the card.
  status: string;                // open | pending | resolved | closed
  category: string;              // core-issue classification
  coreIssue: string;             // the requester's problem statement (description) or subject
  createdAtLabel: string;        // absolute creation date ("Jun 9, 2026")
}

const isUnresolved = (t: Ticket): boolean => t.status === 'open' || t.status === 'pending';

const hasBounce = (comments: Comment[] = []): boolean =>
  comments.some(
    (c) =>
      c.type === 'system-bounce' ||
      (c.delivery && (c.delivery.state === 'bounced' || c.delivery.state === 'partial')),
  );

/** Build the inbox feed from the loaded tickets. `readIds` are ticket ids the
 *  user has already opened/acknowledged. Pass `currentUserId` to flag the items
 *  assigned to the viewing user ("Assigned to you"). */
export function buildInboxItems(tickets: Ticket[], readIds: Set<string>, currentUserId?: string | null): InboxItem[] {
  return (tickets ?? [])
    .filter((t) => !!t.customer?.email)
    .map((t) => {
      const comments = t.comments ?? [];
      const customerComments = comments.filter((c) => c.type === 'customer');
      const last = customerComments[customerComments.length - 1];
      const assignedTo = (t as any).assignedTo ?? null;
      return {
        id: t.id,
        kind: 'message' as const,
        ticketId: t.id,
        ticketSubject: t.subject,
        channel: t.channel,
        sender: t.customer,
        snippet: last?.body ?? t.subject,
        // "X ago" reflects the ticket's real last activity from the API
        // (lastActivityAt → updatedAt → createdAt), never a hardcoded value.
        time: t.lastActivityAgo ?? t.openedAgo,
        timeISO: t.lastActivityISO ?? t.createdAtISO ?? null,
        viaEmail: last?.viaEmail ?? t.channel === 'email',
        unread: isUnresolved(t) && !readIds.has(t.id),
        priority: t.priority,
        watcherBounce: hasBounce(comments),
        commentCount: comments.length,
        assigneeName: t.assignee?.name ?? null,
        assigneeInitials: t.assignee?.initials ?? null,
        assigneeColor: t.assignee?.color ?? null,
        assignedToMe: !!currentUserId && !!assignedTo && assignedTo === currentUserId,
        status: t.status,
        category: t.category || 'General',
        coreIssue: (t.description && t.description.trim()) || t.subject,
        createdAtLabel: t.createdAtLabel || t.openedAgo,
      };
    });
}

/** Count of unread inbox items — the real number behind the sidebar/bell badges. */
export function countUnreadInbox(tickets: Ticket[], readInboxIds: string[] = []): number {
  const read = new Set(readInboxIds);
  return (tickets ?? []).filter((t) => isUnresolved(t) && !read.has(t.id)).length;
}
